import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Facebook,
  Copy,
  CheckCircle2,
  PlayCircle,
  ArrowRight,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import nexaeoLogo from "@/assets/nexaeo-logo.png";

export const Route = createFileRoute("/_authenticated/onboarding")({
  component: OnboardingPage,
});

const BUSINESS_ID = "1234567890123456";

// TODO: Replace with your real values. For Vite, prefer env vars exposed via VITE_ prefix.
const META_APP_ID = (import.meta.env.VITE_META_APP_ID as string) || "YOUR_META_APP_ID";
const N8N_WEBHOOK_URL =
  (import.meta.env.VITE_N8N_WEBHOOK_URL as string) || "YOUR_N8N_WEBHOOK_URL";
const FB_SDK_VERSION = "v19.0";
const FB_GRAPH_BASE = `https://graph.facebook.com/${FB_SDK_VERSION}`;
const FB_SCOPES =
  "public_profile,ads_read,ads_management,business_management,pages_show_list,pages_read_engagement";

type FbPage = { id: string; name: string };
type FbAdAccount = { id: string; name: string; account_id: string };

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

let fbSdkPromise: Promise<void> | null = null;

function loadFacebookSdk(appId: string): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (fbSdkPromise) return fbSdkPromise;

  fbSdkPromise = new Promise<void>((resolve, reject) => {
    if (window.FB) {
      try {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: true,
          version: FB_SDK_VERSION,
        });
      } catch (e) {
        // ignore re-init errors
      }
      resolve();
      return;
    }

    window.fbAsyncInit = () => {
      try {
        window.FB.init({
          appId,
          cookie: true,
          xfbml: true,
          version: FB_SDK_VERSION,
        });
        resolve();
      } catch (e) {
        reject(e);
      }
    };

    const existing = document.getElementById("facebook-jssdk") as HTMLScriptElement | null;
    if (existing) return; // fbAsyncInit will fire

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.onerror = () => reject(new Error("Failed to load Facebook SDK"));
    document.body.appendChild(script);
  });

  return fbSdkPromise;
}

function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [sdkReady, setSdkReady] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [pages, setPages] = useState<FbPage[]>([]);
  const [adAccounts, setAdAccounts] = useState<FbAdAccount[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [page, setPage] = useState("");
  const [adAccount, setAdAccount] = useState("");
  const [finishing, setFinishing] = useState(false);

  // Intentionally no auto-redirect: allow already-onboarded users to revisit
  // this page (e.g. via "Reconnect Meta" in Settings) to reconnect their account.

  useEffect(() => {
    if (META_APP_ID === "YOUR_META_APP_ID") {
      // SDK won't init properly without an app id; surface for the dev but don't crash.
      console.warn("[Meta] VITE_META_APP_ID not set — Facebook SDK will not work until configured.");
    }
    loadFacebookSdk(META_APP_ID)
      .then(() => setSdkReady(true))
      .catch((err) => {
        console.error(err);
        toast.error("Could not load Facebook SDK");
      });
  }, []);

  async function fetchMetaAssets(token: string) {
    setLoadingAssets(true);
    try {
      const [pagesRes, adAcctsRes] = await Promise.all([
        fetch(`${FB_GRAPH_BASE}/me/accounts?access_token=${encodeURIComponent(token)}`),
        fetch(
          `${FB_GRAPH_BASE}/me/adaccounts?fields=name,account_id&access_token=${encodeURIComponent(
            token,
          )}`,
        ),
      ]);

      const pagesJson = await pagesRes.json();
      const adAcctsJson = await adAcctsRes.json();

      if (pagesJson.error) throw new Error(pagesJson.error.message);
      if (adAcctsJson.error) throw new Error(adAcctsJson.error.message);

      setPages((pagesJson.data || []) as FbPage[]);
      setAdAccounts((adAcctsJson.data || []) as FbAdAccount[]);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to load Meta assets");
    } finally {
      setLoadingAssets(false);
    }
  }

  function handleConnect() {
    if (!sdkReady || !window.FB) {
      toast.error("Facebook SDK not ready yet");
      return;
    }
    setConnecting(true);
    window.FB.login(
      (response: any) => {
        setConnecting(false);
        if (response?.status === "connected" && response.authResponse?.accessToken) {
          const token = response.authResponse.accessToken as string;
          setAccessToken(token);
          setConnected(true);
          toast.success("Meta connection established");
          fetchMetaAssets(token);
        } else {
          toast.error("Meta login was cancelled or failed");
        }
      },
      { scope: FB_SCOPES },
    );
  }

  function copyId() {
    navigator.clipboard.writeText(BUSINESS_ID);
    toast.success("Business Manager ID copied");
  }

  async function saveAndNotify(opts: { advanceToStep2?: boolean; goToDashboard?: boolean }) {
    console.log("Button clicked!");
    if (!page || !adAccount) {
      toast.error("Select a page and ad account");
      return;
    }
    setFinishing(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      const user = u.user;
      console.log("Data to send:", {
        user_id: user?.id,
        page: page,
        ad_account: adAccount,
      });
      if (!user) {
        toast.error("Not signed in");
        return;
      }

      console.log("Updating Supabase profile…");
      const { error } = await supabase
        .from("profiles")
        .update({
          onboarded: !!opts.goToDashboard,
          meta_page: page,
          meta_ad_account: adAccount,
        })
        .eq("id", user.id);
      console.log("Supabase update complete", { error });

      if (error) {
        toast.error(error.message);
        return;
      }

      if (N8N_WEBHOOK_URL && N8N_WEBHOOK_URL !== "YOUR_N8N_WEBHOOK_URL") {
        console.log("Calling n8n webhook:", N8N_WEBHOOK_URL);
        const res = await fetch(N8N_WEBHOOK_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: user.id,
            page_id: page,
            ad_account_id: adAccount,
            access_token: accessToken,
          }),
        });
        console.log("n8n webhook response", res.status, res.statusText);
      } else {
        console.warn("[n8n] VITE_N8N_WEBHOOK_URL not configured — skipping webhook");
      }

      if (opts.advanceToStep2) setStep(2);
      if (opts.goToDashboard) {
        toast.success("Welcome aboard");
        navigate({ to: "/dashboard", replace: true });
      }
    } catch (error) {
      console.error("Connection Error:", error);
      toast.error((error as Error)?.message || "Something went wrong");
    } finally {
      setFinishing(false);
    }
  }

  return (
    <main className="relative min-h-screen flex items-center justify-center px-4 py-12">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[500px] pointer-events-none"
        style={{ background: "var(--gradient-glow)" }}
      />
      <div className="relative w-full max-w-2xl">
        <div className="flex items-center justify-center mb-6 bg-transparent">
          <img
            src={nexaeoLogo}
            alt="NexAEO Ventures"
            className="w-56 h-auto object-contain bg-transparent"
          />
        </div>

        {/* Stepper */}
        <div className="mb-6 flex items-center justify-center gap-3 text-xs">
          <StepDot active={step >= 1} done={step > 1} label="System Connection" n={1} />
          <span className="h-px w-12 bg-border" />
          <StepDot active={step >= 2} done={false} label="Agency Access" n={2} />
        </div>

        <div className="surface-panel p-8">
          {step === 1 ? (
            <>
              <h1 className="text-2xl font-semibold">Connect your Meta Ads account</h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Securely link your Facebook Page and Ad Account so we can deliver your campaigns.
              </p>

              {!connected ? (
                <div className="mt-8 flex flex-col items-center text-center">
                  <Button
                    onClick={handleConnect}
                    variant="meta"
                    size="xl"
                    disabled={connecting || !sdkReady}
                    className="min-w-[260px]"
                  >
                    {connecting || !sdkReady ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Facebook className="h-5 w-5" />
                    )}
                    {!sdkReady
                      ? "Loading Facebook…"
                      : connecting
                        ? "Connecting…"
                        : "Connect Meta Ads"}
                  </Button>
                  <p className="mt-4 max-w-sm text-xs text-muted-foreground">
                    You'll be redirected to Meta to authorize NexAEO Ventures. We never store your
                    credentials.
                  </p>
                </div>
              ) : (
                <div className="mt-6 space-y-5">
                  <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">
                    <CheckCircle2 className="h-4 w-4" />
                    Authorized with Meta. Select the assets to use.
                  </div>

                  {loadingAssets ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading your pages and ad
                      accounts…
                    </div>
                  ) : (
                    <>
                      <Selector
                        label="Facebook Page"
                        placeholder={
                          pages.length ? "Choose a page" : "No pages found on this account"
                        }
                        value={page}
                        onChange={setPage}
                        options={pages.map((p) => ({ value: p.id, label: p.name }))}
                        disabled={pages.length === 0}
                      />
                      <Selector
                        label="Ad Account"
                        placeholder={
                          adAccounts.length
                            ? "Choose an ad account"
                            : "No ad accounts found on this account"
                        }
                        value={adAccount}
                        onChange={setAdAccount}
                        options={adAccounts.map((a) => ({
                          value: a.account_id,
                          label: `${a.name} (act_${a.account_id})`,
                        }))}
                        disabled={adAccounts.length === 0}
                      />
                      {accessToken && (pages.length === 0 || adAccounts.length === 0) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchMetaAssets(accessToken)}
                        >
                          Retry fetch
                        </Button>
                      )}
                    </>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      variant="hero"
                      size="lg"
                      disabled={!page || !adAccount || finishing}
                      onClick={(e) => {
                        e.preventDefault();
                        void saveAndNotify({ advanceToStep2: true });
                      }}
                    >
                      {finishing && <Loader2 className="animate-spin" />}
                      Continue <ArrowRight />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-success/15 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-success" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold">Partner request sent</h1>
                  <p className="text-sm text-muted-foreground">
                    We've sent a Partner Request to your Meta Business Manager.
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-border bg-background/40 p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  NexAEO Business Manager ID
                </p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <code className="font-mono text-lg tracking-wider">{BUSINESS_ID}</code>
                  <Button variant="outline" size="sm" onClick={copyId}>
                    <Copy className="h-3.5 w-3.5" /> Copy ID
                  </Button>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-medium">
                  How to approve our partner request in 30 seconds
                </h3>
                <div className="mt-2 aspect-video w-full rounded-lg border border-border bg-gradient-to-br from-card to-background flex items-center justify-center group cursor-pointer hover:border-primary/40 transition">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground group-hover:text-foreground transition">
                    <PlayCircle className="h-12 w-12" />
                    <span className="text-xs">Watch instructional video</span>
                  </div>
                </div>
              </div>

              <ol className="mt-6 space-y-2 text-sm text-muted-foreground">
                <li>1. Open Meta Business Manager → Settings → Partners.</li>
                <li>
                  2. Find the pending request from{" "}
                  <span className="text-foreground">NexAEO Ventures</span>.
                </li>
                <li>3. Approve the request — you're all set.</li>
              </ol>

              <div className="mt-8 flex items-center justify-between gap-3">
                <Button variant="ghost" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button
                  type="button"
                  variant="hero"
                  size="lg"
                  onClick={(e) => {
                    e.preventDefault();
                    void saveAndNotify({ goToDashboard: true });
                  }}
                  disabled={finishing}
                >
                  {finishing && <Loader2 className="animate-spin" />}
                  Go to Dashboard <ArrowRight />
                </Button>
              </div>
            </>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Need help?{" "}
          <button
            type="button"
            className="cursor-pointer underline underline-offset-4 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={finishing}
            onClick={async () => {
              try {
                const { data: u } = await supabase.auth.getUser();
                const user = u.user;
                if (!user) {
                  navigate({ to: "/dashboard", replace: true });
                  return;
                }
                const { error } = await supabase
                  .from("profiles")
                  .update({ onboarded: true })
                  .eq("id", user.id);
                if (error) {
                  toast.error(error.message);
                  return;
                }
                navigate({ to: "/dashboard", replace: true });
              } catch (err) {
                console.error("Skip onboarding error:", err);
                toast.error((err as Error)?.message || "Could not skip onboarding");
              }
            }}
          >
            Skip for now
          </button>
        </p>
      </div>
    </main>
  );
}

function StepDot({
  active,
  done,
  label,
  n,
}: {
  active: boolean;
  done: boolean;
  label: string;
  n: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-6 w-6 rounded-full grid place-items-center text-[11px] font-semibold border ${
          done
            ? "bg-success text-success-foreground border-success"
            : active
              ? "hero-gradient text-primary-foreground border-transparent"
              : "border-border text-muted-foreground"
        }`}
      >
        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
      </div>
      <span className={active ? "text-foreground" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}

function Selector({
  label,
  placeholder,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
